import { orderBetween, type ListItem, type ListMeta } from '@awwyeah/shared';
import type * as Y from 'yjs';

/**
 * Pure(-ish) mutation helpers for a `list:<id>` Yjs doc. Kept free of React so the
 * reorder-under-concurrency guarantee (ROADMAP §2 Phase 1 Lists) is unit-testable against
 * plain `Y.Doc`s — see `apps/web/test/modules/lists/listOps.test.ts`.
 *
 * Doc shape (ROADMAP §1.3): `order` is a `Y.Array<string>` of item ids — it exists so a
 * list's ids can be enumerated/iterated without touching content, and so a delete can
 * remove an id in one place. It is **not** the source of display order: each item carries
 * its own fractional `order` number (`orderBetween`), and *that* field is what reordering
 * writes. A reorder is therefore a single `Y.Map` field replace (last-write-wins, always
 * safe) rather than a `Y.Array` structural move — the operation class that can duplicate
 * or lose entries under concurrent edits. Concurrently deleting a different item, editing
 * text, or reordering the same item on two clients can never lose data with this split.
 */

export function newId(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function nowIso(): string {
  return new Date().toISOString();
}

/** Items in display order (ascending fractional `order`), ignoring any id missing from the map. */
export function getOrderedItems(order: Y.Array<string>, map: Y.Map<ListItem>): ListItem[] {
  const items: ListItem[] = [];
  for (const id of order.toArray()) {
    const item = map.get(id);
    if (item) items.push(item);
  }
  return items.sort((a, b) => a.order - b.order);
}

export interface NewItemInput {
  text: string;
  userId: string;
  header?: boolean;
  section?: string;
  detail?: string;
  assigneeIds?: string[];
}

/** Appends a new item at the end of the current order and returns its id. */
export function addItem(
  doc: Y.Doc,
  order: Y.Array<string>,
  map: Y.Map<ListItem>,
  input: NewItemInput,
): string {
  const id = newId();
  const at = nowIso();
  const last = getOrderedItems(order, map).at(-1);
  const item: ListItem = {
    id,
    text: input.text,
    checked: false,
    assigneeIds: input.assigneeIds ?? [],
    header: input.header,
    section: input.section,
    detail: input.detail,
    order: orderBetween(last ? last.order : null, null),
    createdBy: input.userId,
    createdAt: at,
    updatedBy: input.userId,
    updatedAt: at,
  };
  doc.transact(() => {
    map.set(id, item);
    order.push([id]);
  });
  return id;
}

/**
 * Moves `itemId` so its `order` sits between `beforeId`/`afterId`'s current order values
 * (either may be `undefined` at a list boundary). Writes only the moved item's `order`
 * field — see the module doc comment for why that's what makes this concurrency-safe.
 */
export function reorderItem(
  doc: Y.Doc,
  map: Y.Map<ListItem>,
  itemId: string,
  beforeId: string | undefined,
  afterId: string | undefined,
  userId: string,
): void {
  const item = map.get(itemId);
  if (!item) return;
  const before = beforeId ? (map.get(beforeId)?.order ?? null) : null;
  const after = afterId ? (map.get(afterId)?.order ?? null) : null;
  doc.transact(() => {
    map.set(itemId, {
      ...item,
      order: orderBetween(before, after),
      updatedBy: userId,
      updatedAt: nowIso(),
    });
  });
}

export function toggleItem(doc: Y.Doc, map: Y.Map<ListItem>, itemId: string, userId: string): void {
  const item = map.get(itemId);
  if (!item) return;
  const checked = !item.checked;
  const at = nowIso();
  doc.transact(() => {
    map.set(itemId, {
      ...item,
      checked,
      checkedBy: checked ? userId : undefined,
      checkedAt: checked ? at : undefined,
      updatedBy: userId,
      updatedAt: at,
    });
  });
}

export function updateItem(
  doc: Y.Doc,
  map: Y.Map<ListItem>,
  itemId: string,
  userId: string,
  patch: Partial<Pick<ListItem, 'text' | 'assigneeIds' | 'section' | 'detail'>>,
): void {
  const item = map.get(itemId);
  if (!item) return;
  doc.transact(() => {
    map.set(itemId, { ...item, ...patch, updatedBy: userId, updatedAt: nowIso() });
  });
}

/** Removes an item's id from `order` and its entry from `map` in one transaction. */
export function deleteItem(
  doc: Y.Doc,
  order: Y.Array<string>,
  map: Y.Map<ListItem>,
  itemId: string,
): void {
  doc.transact(() => {
    const ids = order.toArray();
    const index = ids.indexOf(itemId);
    if (index !== -1) order.delete(index, 1);
    map.delete(itemId);
  });
}

/**
 * Checked-sink ordering (ROADMAP §2.4): unchecked items keep their fractional order;
 * checked items sink beneath them, most-recently-checked first (what you just did should
 * be easy to find and undo).
 */
export function splitCheckedUnchecked(items: ListItem[]): {
  unchecked: ListItem[];
  checked: ListItem[];
} {
  const real = items.filter((item) => !item.header);
  const unchecked = real.filter((item) => !item.checked).sort((a, b) => a.order - b.order);
  const checked = real
    .filter((item) => item.checked)
    .sort((a, b) => (b.checkedAt ?? '').localeCompare(a.checkedAt ?? ''));
  return { unchecked, checked };
}

/**
 * Writes a template's selected items into a (usually freshly created) list doc, one
 * header item per non-empty group. `selectedKeys` holds `${groupIndex}-${itemIndex}`
 * strings — see `templates.ts` / `TemplatePickerPage.tsx`.
 */
export function instantiateTemplate(
  doc: Y.Doc,
  order: Y.Array<string>,
  map: Y.Map<ListItem>,
  groups: { name: string; items: { text: string; detail?: string }[] }[],
  selectedKeys: Set<string>,
  userId: string,
): void {
  doc.transact(() => {
    groups.forEach((group, groupIndex) => {
      const picked = group.items.filter((_, itemIndex) =>
        selectedKeys.has(`${groupIndex}-${itemIndex}`),
      );
      if (picked.length === 0) return;
      addItem(doc, order, map, { text: group.name, userId, header: true });
      for (const item of picked) {
        addItem(doc, order, map, { text: item.text, detail: item.detail, userId });
      }
    });
  });
}

export interface HeaderSegment {
  header: ListItem | null;
  items: ListItem[];
}

/**
 * Groups unchecked (+ header) items by the manual header rows the household added, in
 * order. Leading items before the first header land in a `header: null` segment. Checked
 * items never appear here — they sink into the flat "Done" section (Screen D §11).
 */
export function buildHeaderSegments(items: ListItem[]): HeaderSegment[] {
  const relevant = items
    .filter((item) => item.header || !item.checked)
    .sort((a, b) => a.order - b.order);
  const segments: HeaderSegment[] = [{ header: null, items: [] }];
  for (const item of relevant) {
    if (item.header) {
      segments.push({ header: item, items: [] });
    } else {
      segments[segments.length - 1]?.items.push(item);
    }
  }
  return segments.filter((s) => s.header || s.items.length > 0);
}

export interface SectionGroup {
  name: string;
  items: ListItem[];
}

/** Groups shopping items by their `section` (store aisle) tag; unsectioned items sort first. */
export function buildSectionGroups(items: ListItem[]): SectionGroup[] {
  const byName = new Map<string, ListItem[]>();
  for (const item of items.filter((i) => !i.header).sort((a, b) => a.order - b.order)) {
    const key = item.section ?? '';
    const list = byName.get(key) ?? [];
    list.push(item);
    byName.set(key, list);
  }
  return [...byName.entries()]
    .sort(([a], [b]) => (a === '' ? -1 : b === '' ? 1 : a.localeCompare(b)))
    .map(([name, groupItems]) => ({ name: name || 'Everything else', items: groupItems }));
}

export function createListMeta(input: {
  name: string;
  type: ListMeta['type'];
  userId: string;
  store?: string;
  emoji?: string;
  sectionsEnabled?: boolean;
}): ListMeta {
  const at = nowIso();
  return {
    id: newId(),
    name: input.name,
    type: input.type,
    store: input.store,
    emoji: input.emoji,
    archived: false,
    sectionsEnabled: input.sectionsEnabled,
    createdBy: input.userId,
    createdAt: at,
    updatedBy: input.userId,
    updatedAt: at,
  };
}

/**
 * Splits pasted/typed bulk input into individual item texts. Newlines always separate
 * items; commas also separate ("milk, eggs, bread" → three items). A lone line with no
 * separators comes back as a single-element array. Empty fragments are dropped.
 */
export function splitListInput(raw: string): string[] {
  return raw
    .split(/\r?\n|,/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

/**
 * Moves a list's **metadata** between the shared `lists` registry and a member's personal
 * `lists:user:<id>` one (WS-15). The list's *content* doc (`list:<id>`) never moves — it
 * keeps its name forever and its ACL row is what actually protects it.
 *
 * The registry move exists purely so **titles** don't leak: a private list whose meta sat
 * in the shared registry would be a locked door with its name written on it. Everyone can
 * open `lists`, so anything in `lists` is public knowledge.
 *
 * Two docs cannot share one Yjs transaction, so this is deliberately ordered
 * **write-then-delete**: the worst case of a crash between the two is the list appearing
 * in both registries for a moment (the index de-dupes by id, personal winning), which is
 * strictly better than it appearing in neither and looking deleted.
 */
export function moveListMeta(
  from: Y.Map<ListMeta>,
  to: Y.Map<ListMeta>,
  meta: ListMeta,
  userId?: string,
): void {
  if (from === to) return;
  const stamped: ListMeta = userId
    ? { ...meta, updatedBy: userId, updatedAt: nowIso() }
    : { ...meta };
  to.set(stamped.id, stamped);
  from.delete(stamped.id);
}

/**
 * Which registry a list belongs in for a given audience: only `family` lives in the shared
 * one. `members` sharing is done at the ACL, but the meta still moves out of `lists` —
 * otherwise the *other* half of the household would read the title of a list they can't
 * open, which is precisely the leak this whole mechanism exists to close.
 */
export function registryForAudience(
  audience: 'family' | 'private' | 'members',
): 'family' | 'personal' {
  return audience === 'family' ? 'family' : 'personal';
}

/** Key inside a `list:<id>` content doc holding a mirror of that list's `ListMeta`. */
export const LIST_META_KEY = 'meta';
const LIST_META_ENTRY = 'list';

/**
 * Mirrors a list's metadata into its own content doc (WS-15).
 *
 * Registries answer "which lists exist for me" — but you can only read a registry you can
 * open, and a list shared *with* you lives in its owner's personal registry, which the
 * sync hub rightly refuses you. So a member you've shared with could open `list:<id>` and
 * still not know the list's name.
 *
 * The fix is to keep a copy of the meta inside the content doc, which is protected by
 * exactly the ACL that decides who may see the list in the first place. `GET /api/sharing`
 * tells a member *which* docs are shared with them; this is where the name comes from.
 * The registries remain the source of truth for enumeration and for the owner's own view.
 */
export function mirrorListMeta(contentDoc: Y.Doc, meta: ListMeta): void {
  contentDoc.getMap<ListMeta>(LIST_META_KEY).set(LIST_META_ENTRY, meta);
}

/** Reads the mirror back, or `null` when a doc predates it / was never mirrored. */
export function readListMetaMirror(contentDoc: Y.Doc): ListMeta | null {
  const value = contentDoc.getMap<ListMeta>(LIST_META_KEY).get(LIST_META_ENTRY);
  return value ?? null;
}
